import { Suspense, useState } from "react";

import dayjs from "@/lib/dayjs";
import { HostReceiptType } from "@/lib/gql/__generated__/graphql";

import { BlankContentForList } from "@/components/features/_components";
import { FeatureIslandContainer, FeatureIslandMainWithBackGround } from "@/components/features/_layouts";

import { Button } from "@/components/ui/button/button";
import type { DateRange } from "@/components/ui/calendar/types/calendar-range";
import type { FormOptionType } from "@/components/ui/form/_types/type";
import { SelectField } from "@/components/ui/form/fields/select-field";
import Help from "@/components/ui/icon/help";
import { DatePickerRange } from "@/components/ui/input/date-picker-range";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table/table";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip/tooltip";

import { SalesHeaderAndTab } from "../../_shared";
import { useHostReceiptList } from "../model/repository/query/host-receipt-list";
import { filterByIssuedDate } from "../model/use-case/filter/filter-by-issued-date";
import { ALL_RECEIPT_TYPES, filterByReceiptType } from "../model/use-case/filter/filter-by-receipt-type";
import { useDownloadReceipt } from "../model/use-case/use-download-receipt";

import type { ReceiptTypeFilter } from "../model/use-case/filter/filter-by-receipt-type";

type Props = {
	hotelID: string;
};

const FORMAT_DATE_TEMPLATE = "YYYY年MM月DD日";

const RECEIPT_TYPE_LABEL: Record<HostReceiptType, string> = {
	[HostReceiptType.HandlingFee]: "予約手数料",
	[HostReceiptType.BankTransferFee]: "振込手数料",
	[HostReceiptType.LocalPayment]: "現地決済手数料",
	[HostReceiptType.UsageFee]: "システム利用料",
};

// 種類ごとに何の領収書かを画面で説明する
const RECEIPT_TYPE_DESCRIPTION: Record<HostReceiptType, string> = {
	[HostReceiptType.HandlingFee]:
		"事前決済の予約にかかる手数料（従量課金とカード決済手数料）の、入金 1 回分の合計です。入金のたびに発行します。",
	[HostReceiptType.BankTransferFee]: "売上を施設の口座へ振り込むときの手数料です。入金のたびに発行します。",
	[HostReceiptType.LocalPayment]: "現地決済の予約にかかる手数料です。手数料をまとめて徴収するたびに発行します。",
	[HostReceiptType.UsageFee]: "基本料金や定額チケットの利用料です。チケットを購入するたびに発行します。",
};

const RECEIPT_TYPE_OPTIONS: FormOptionType[] = [
	{ id: ALL_RECEIPT_TYPES, label: "全て", value: ALL_RECEIPT_TYPES },
	...Object.entries(RECEIPT_TYPE_LABEL).map(([value, label]) => ({ id: value, label, value })),
];

/**
 * 種類ごとに何の領収書かを説明する
 */
const ReceiptTypeHelp = () => (
	<TooltipProvider>
		<Tooltip>
			<TooltipTrigger type="button" className="cursor-default">
				<Help size="sm" />
			</TooltipTrigger>
			<TooltipContent side="bottom" sideOffset={10}>
				<dl className="flex max-w-sm flex-col gap-2 p-3 text-left font-normal">
					{Object.entries(RECEIPT_TYPE_LABEL).map(([type, label]) => (
						<div key={type}>
							<dt className="font-semibold">{label}</dt>
							<dd>{RECEIPT_TYPE_DESCRIPTION[type as HostReceiptType]}</dd>
						</div>
					))}
				</dl>
			</TooltipContent>
		</Tooltip>
	</TooltipProvider>
);

const ReceiptListTable = ({ hotelID }: Props) => {
	const { data } = useHostReceiptList(hotelID);
	const { onDownloadReceipt } = useDownloadReceipt(hotelID);

	const [receiptType, setReceiptType] = useState<ReceiptTypeFilter>(ALL_RECEIPT_TYPES);
	const [issuedDateRange, setIssuedDateRange] = useState<DateRange>({ from: undefined, to: undefined });

	const allRows = data?.hostReceiptList.list ?? [];
	const rows = filterByIssuedDate(filterByReceiptType(allRows, receiptType), issuedDateRange);

	if (allRows.length === 0) {
		return <BlankContentForList content="CHILLNN から発行した領収書はまだありません。" />;
	}

	return (
		<div className="space-y-6">
			<div className="grid grid-cols-1 items-center gap-4 tablet:flex">
				{/* 種類での絞り込み */}
				<SelectField
					value={receiptType}
					options={RECEIPT_TYPE_OPTIONS}
					handleValueChange={(value) => setReceiptType(value as ReceiptTypeFilter)}
				/>

				{/* 発行日での絞り込み */}
				<DatePickerRange
					selected={issuedDateRange}
					onSelect={setIssuedDateRange}
					showPresetOptions="exclude-future-options"
					canSelectPastDate
					className="w-full tablet:w-80"
				/>
			</div>

			{rows.length === 0 ? (
				<BlankContentForList content={`該当する領収書がありません。\n種類や期間を変更してください。`} />
			) : (
				<div className="w-full shadow-sm">
					<Table className="w-full min-w-[max-content]">
						<TableHeader className="relative bg-bg-layer-400">
							<TableRow className="hover:bg-transparent">
								<TableHead justifyStyle="center" className="font-semibold">
									発行日
								</TableHead>
								<TableHead justifyStyle="center" className="font-semibold">
									<div className="flex items-center gap-x-1">
										種類
										<ReceiptTypeHelp />
									</div>
								</TableHead>
								<TableHead justifyStyle="center" className="font-semibold"></TableHead>
							</TableRow>
						</TableHeader>
						<TableBody>
							{rows.map((row) => (
								<TableRow key={row.id}>
									<TableCell className="text-center">
										{dayjs(row.issuedAt).format(FORMAT_DATE_TEMPLATE)}
									</TableCell>
									<TableCell className="text-center">{RECEIPT_TYPE_LABEL[row.receiptType]}</TableCell>
									<TableCell className="text-center">
										{row.isDownloadable ? (
											<Button
												type="button"
												variant="outline"
												onClick={() => onDownloadReceipt(row.receiptType, row.uniqueID)}
											>
												ダウンロード
											</Button>
										) : (
											<span className="text-sm text-zinc-500">
												発行時のメールからご確認ください
											</span>
										)}
									</TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>
				</div>
			)}
		</div>
	);
};

/**
 * CHILLNN が施設に発行した領収書（手数料・利用料）の一覧
 */
export const ManagementSalesReceipts = ({ hotelID }: Props) => (
	<FeatureIslandContainer>
		<SalesHeaderAndTab hotelID={hotelID} />

		<FeatureIslandMainWithBackGround>
			<Suspense fallback={<p className="text-sm">読み込み中...</p>}>
				<ReceiptListTable hotelID={hotelID} />
			</Suspense>
		</FeatureIslandMainWithBackGround>
	</FeatureIslandContainer>
);

ManagementSalesReceipts.displayName = "ManagementSalesReceipts";
